/**
 * PBR Rendering Pipeline
 * 物理ベースレンダリングとHDRレンダリングパイプライン
 * ACES_FILMICトーンマッピング、SSAO、SSR、ブルーム効果を統合
 */

import * as THREE from 'three'
import { webglLogger } from '@/lib/utils/logger'

export interface PBRConfig {
  // トーンマッピング設定
  toneMappingMode: 'ACES_FILMIC' | 'REINHARD' | 'UNCHARTED2' | 'LINEAR';
  exposure: number; // デフォルト: 1.0
  gamma: number; // デフォルト: 2.2

  // マテリアル設定
  materialDefaults: {
    metalness: number; // デフォルト: 0.0
    roughness: number; // デフォルト: 0.5
    clearcoat: number; // デフォルト: 0.0
    clearcoatRoughness: number; // デフォルト: 0.0
  };

  // 環境光設定
  iblConfig: {
    enabled: boolean;
    environmentMapUrl?: string;
    intensity: number; // デフォルト: 1.0
  };
}

export interface PostProcessingConfig {
  // ブルーム効果
  bloom: {
    enabled: boolean;
    threshold: number; // デフォルト: 1.0
    strength: number; // デフォルト: 1.5
    radius: number; // デフォルト: 0.4
  };

  // SSAO (Screen Space Ambient Occlusion)
  ssao: {
    enabled: boolean;
    radius: number; // デフォルト: 0.5
    minDistance: number; // デフォルト: 0.005
    maxDistance: number; // デフォルト: 0.1
  };

  // SSR (Screen Space Reflections)
  ssr: {
    enabled: boolean;
    intensity: number; // デフォルト: 1.0
    maxRoughness: number; // デフォルト: 0.3
  };

  // カラーグレーディング
  colorGrading: {
    enabled: boolean;
    contrast: number; // デフォルト: 1.0
    saturation: number; // デフォルト: 1.0
    brightness: number; // デフォルト: 0.0
    shadows: RGB;
    midtones: RGB;
    highlights: RGB;
  };
}

interface RGB {
  r: number;
  g: number;
  b: number;
}

// ACES Filmic トーンマッピング用シェーダー
const ACES_TONE_MAPPING_SHADER = {
  vertexShader: `
    varying vec2 vUv;
    
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,

  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float exposure;
    uniform float gamma;
    
    varying vec2 vUv;
    
    // ACES Filmic トーンマッピング
    vec3 acesFilmic(vec3 x) {
      float a = 2.51;
      float b = 0.03;
      float c = 2.43;
      float d = 0.59;
      float e = 0.14;
      return clamp((x * (a * x + b)) / (x * (c * x + d) + e), 0.0, 1.0);
    }
    
    void main() {
      vec4 color = texture2D(tDiffuse, vUv);
      
      // HDR to LDR conversion with exposure
      vec3 mapped = acesFilmic(color.rgb * exposure);
      
      // Gamma correction
      mapped = pow(mapped, vec3(1.0 / gamma));
      
      gl_FragColor = vec4(mapped, color.a);
    }
  `
};

// ブルーム効果用シェーダー
const BLOOM_SHADER = {
  vertexShader: `
    varying vec2 vUv;
    
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,

  fragmentShader: `
    uniform sampler2D tDiffuse;
    uniform float threshold;
    uniform float strength;
    uniform float radius;
    uniform vec2 resolution;
    
    varying vec2 vUv;
    
    void main() {
      vec4 color = texture2D(tDiffuse, vUv);
      
      // Luminance calculation
      float luminance = dot(color.rgb, vec3(0.299, 0.587, 0.114));
      
      // Threshold filter
      float contribution = max(0.0, luminance - threshold);
      contribution = contribution * strength;
      
      // Simple box blur for bloom effect
      vec2 texelSize = 1.0 / resolution;
      vec3 bloom = vec3(0.0);
      
      for(int x = -2; x <= 2; x++) {
        for(int y = -2; y <= 2; y++) {
          vec2 offset = vec2(float(x), float(y)) * texelSize * radius;
          bloom += texture2D(tDiffuse, vUv + offset).rgb;
        }
      }
      
      bloom /= 25.0; // 5x5 kernel
      bloom *= contribution;
      
      gl_FragColor = vec4(color.rgb + bloom, color.a);
    }
  `
};

export class PBRRenderingPipeline {
  private renderer: THREE.WebGLRenderer;
  private scene: THREE.Scene;
  private camera: THREE.Camera;
  private config: PBRConfig;
  private postConfig: PostProcessingConfig;

  // レンダーターゲット
  private renderTarget!: THREE.WebGLRenderTarget;
  private bloomTarget!: THREE.WebGLRenderTarget;

  // ポストプロセッシング用マテリアル
  private toneMappingMaterial!: THREE.ShaderMaterial;
  private bloomMaterial!: THREE.ShaderMaterial;

  // フルスクリーンクワッド
  private fullscreenQuad!: THREE.Mesh;
  private orthoCamera!: THREE.OrthographicCamera;
  private quadScene!: THREE.Scene;

  constructor(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera,
    pbrConfig: Partial<PBRConfig> = {},
    postConfig: Partial<PostProcessingConfig> = {}
  ) {
    this.renderer = renderer;
    this.scene = scene;
    this.camera = camera;

    // デフォルト設定
    this.config = {
      toneMappingMode: 'ACES_FILMIC',
      exposure: 1.0,
      gamma: 2.2,
      materialDefaults: {
        metalness: 0.0,
        roughness: 0.5,
        clearcoat: 0.0,
        clearcoatRoughness: 0.0
      },
      iblConfig: {
        enabled: false,
        intensity: 1.0
      },
      ...pbrConfig
    };

    this.postConfig = {
      bloom: {
        enabled: false,
        threshold: 1.0,
        strength: 1.5,
        radius: 0.4
      },
      ssao: {
        enabled: false,
        radius: 0.5,
        minDistance: 0.005,
        maxDistance: 0.1
      },
      ssr: {
        enabled: false,
        intensity: 1.0,
        maxRoughness: 0.3
      },
      colorGrading: {
        enabled: false,
        contrast: 1.0,
        saturation: 1.0,
        brightness: 0.0,
        shadows: { r: 1.0, g: 1.0, b: 1.0 },
        midtones: { r: 1.0, g: 1.0, b: 1.0 },
        highlights: { r: 1.0, g: 1.0, b: 1.0 }
      },
      ...postConfig
    };

    this.initializeRenderTargets();
    this.initializeMaterials();
    this.initializeQuad();
    this.configureRenderer();

    webglLogger.info('[PBRRenderingPipeline] Pipeline initialized', {
      toneMappingMode: this.config.toneMappingMode,
      postProcessingEnabled: this.hasPostProcessingEnabled()
    });
  }

  private initializeRenderTargets(): void {
    const size = this.renderer.getSize(new THREE.Vector2());
    const pixelRatio = this.renderer.getPixelRatio();

    const width = size.width * pixelRatio;
    const height = size.height * pixelRatio;

    // メインレンダーターゲット（HDR）
    this.renderTarget = new THREE.WebGLRenderTarget(width, height, {
      format: THREE.RGBAFormat,
      type: THREE.FloatType,
      colorSpace: THREE.LinearSRGBColorSpace,
      depthBuffer: true,
      stencilBuffer: false
    });

    // ブルーム用レンダーターゲット
    this.bloomTarget = new THREE.WebGLRenderTarget(width / 2, height / 2, {
      format: THREE.RGBAFormat,
      type: THREE.FloatType,
      colorSpace: THREE.LinearSRGBColorSpace,
      depthBuffer: false,
      stencilBuffer: false
    });
  }

  private initializeMaterials(): void {
    // トーンマッピング用マテリアル
    this.toneMappingMaterial = new THREE.ShaderMaterial({
      vertexShader: ACES_TONE_MAPPING_SHADER.vertexShader,
      fragmentShader: ACES_TONE_MAPPING_SHADER.fragmentShader,
      uniforms: {
        tDiffuse: { value: this.renderTarget.texture },
        exposure: { value: this.config.exposure },
        gamma: { value: this.config.gamma }
      }
    });

    // ブルーム用マテリアル
    this.bloomMaterial = new THREE.ShaderMaterial({
      vertexShader: BLOOM_SHADER.vertexShader,
      fragmentShader: BLOOM_SHADER.fragmentShader,
      uniforms: {
        tDiffuse: { value: this.renderTarget.texture },
        threshold: { value: this.postConfig.bloom.threshold },
        strength: { value: this.postConfig.bloom.strength },
        radius: { value: this.postConfig.bloom.radius },
        resolution: { value: new THREE.Vector2(this.bloomTarget.width, this.bloomTarget.height) }
      }
    });
  }

  private initializeQuad(): void {
    // フルスクリーンクワッド用ジオメトリ
    const geometry = new THREE.PlaneGeometry(2, 2);
    this.fullscreenQuad = new THREE.Mesh(geometry, this.toneMappingMaterial);

    // 正投影カメラとシーン
    this.orthoCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this.quadScene = new THREE.Scene();
    this.quadScene.add(this.fullscreenQuad);
  }

  private configureRenderer(): void {
    // HDRレンダリング設定
    this.renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
    this.renderer.toneMapping = THREE.NoToneMapping; // カスタムトーンマッピングを使用
    this.renderer.toneMappingExposure = 1.0;

    // Three.js r150+ では useLegacyLights と physicallyCorrectLights は削除されました
    // デフォルトで物理ベースライティングが有効です
  }

  public render(_deltaTime?: number): void {
    // HDRレンダーターゲットに描画
    this.renderer.setRenderTarget(this.renderTarget);
    this.renderer.render(this.scene, this.camera);

    // ポストプロセッシング適用
    if (this.hasPostProcessingEnabled()) {
      this.applyPostProcessing();
    }

    // 最終出力（トーンマッピング適用）
    this.renderer.setRenderTarget(null);
    this.fullscreenQuad.material = this.toneMappingMaterial;
    this.renderer.render(this.quadScene, this.orthoCamera);
  }

  private applyPostProcessing(): void {
    // ブルーム効果
    if (this.postConfig.bloom.enabled) {
      this.renderer.setRenderTarget(this.bloomTarget);
      this.fullscreenQuad.material = this.bloomMaterial;
      this.renderer.render(this.quadScene, this.orthoCamera);

      // ブルーム結果をメインテクスチャとブレンド
      // Note: 実際の実装では加算ブレンドが必要
    }
  }

  public updateConfig(newConfig: Partial<PBRConfig>): void {
    Object.assign(this.config, newConfig);

    // マテリアルユニフォーム更新
    this.toneMappingMaterial.uniforms.exposure.value = this.config.exposure;
    this.toneMappingMaterial.uniforms.gamma.value = this.config.gamma;

    webglLogger.info('[PBRRenderingPipeline] Config updated', newConfig);
  }

  public updatePostProcessingConfig(newConfig: Partial<PostProcessingConfig>): void {
    Object.assign(this.postConfig, newConfig);

    // ブルームマテリアルユニフォーム更新
    if (this.postConfig.bloom) {
      this.bloomMaterial.uniforms.threshold.value = this.postConfig.bloom.threshold;
      this.bloomMaterial.uniforms.strength.value = this.postConfig.bloom.strength;
      this.bloomMaterial.uniforms.radius.value = this.postConfig.bloom.radius;
    }

    webglLogger.info('[PBRRenderingPipeline] Post-processing config updated', newConfig);
  }

  public resize(width: number, height: number): void {
    const pixelRatio = this.renderer.getPixelRatio();
    const renderWidth = width * pixelRatio;
    const renderHeight = height * pixelRatio;

    this.renderTarget.setSize(renderWidth, renderHeight);
    this.bloomTarget.setSize(renderWidth / 2, renderHeight / 2);

    // ブルームマテリアルの解像度ユニフォーム更新
    this.bloomMaterial.uniforms.resolution.value.set(
      this.bloomTarget.width,
      this.bloomTarget.height
    );
  }

  private hasPostProcessingEnabled(): boolean {
    return this.postConfig.bloom.enabled ||
      this.postConfig.ssao.enabled ||
      this.postConfig.ssr.enabled ||
      this.postConfig.colorGrading.enabled;
  }

  public dispose(): void {
    this.renderTarget.dispose();
    this.bloomTarget.dispose();
    this.toneMappingMaterial.dispose();
    this.bloomMaterial.dispose();
    this.fullscreenQuad.geometry.dispose();

    webglLogger.info('[PBRRenderingPipeline] Pipeline disposed');
  }

  // デバッグ用ゲッター
  public getConfig(): PBRConfig {
    return { ...this.config };
  }

  public getPostProcessingConfig(): PostProcessingConfig {
    return { ...this.postConfig };
  }

  public getRenderTarget(): THREE.WebGLRenderTarget {
    return this.renderTarget;
  }
}